import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Clipboard, Users } from 'lucide-react'
import { api, jsonBody } from '../api'
import type { Locale } from '../i18n'
import type { User } from '../types'

type Member = User & { state: string; passwordSet: boolean; invitationExpiresAt: string | null }
type Invitation = { invitationUrl: string; expiresAt: string }

export function TeamAccess({ locale, user }: { locale: Locale; user: User }) {
  const zh = locale === 'zh-CN'
  const [members, setMembers] = useState<Member[]>([])
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const load = useCallback(async () => {
    const result = await api<{ members: Member[] }>('/api/auth/members')
    setMembers(result.members)
  }, [])
  useEffect(() => { if (!user.isMember) queueMicrotask(() => void load().catch(caught => setError(caught.message))) }, [load, user.isMember])

  async function perform(action: () => Promise<void>) {
    setBusy(true); setError(''); setNotice(''); setInvitation(null)
    try { await action(); if (!user.isMember) await load() } catch (caught) { setError(caught instanceof Error ? caught.message : 'Request failed.') } finally { setBusy(false) }
  }
  const create = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const data = Object.fromEntries(new FormData(form))
    void perform(async () => {
      setInvitation(await api<Invitation>('/api/auth/members', { method: 'POST', body: jsonBody(data) }))
      form.reset()
    })
  }
  const changePassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = Object.fromEntries(new FormData(event.currentTarget))
    if (data.password !== data.confirmPassword) { setError(zh ? '两次密码不一致。' : 'Passwords do not match.'); return }
    void perform(async () => {
      await api('/api/auth/member-password', { method: 'POST', body: jsonBody(data) })
      window.location.assign('/login/password')
    })
  }
  return <section className="content-section team-access">
    <div className="section-title"><div><h2>{user.isMember ? (zh ? '成员密码' : 'Member password') : (zh ? '团队成员' : 'Team members')}</h2><p>{user.isMember ? (zh ? '你使用独立登录账号，共享所属账号的数据和权限。' : 'Your own login shares the account’s data and permissions.') : (zh ? '邀请同事设置自己的密码，长期登录同一后台。仅账号所有者可管理成员。' : 'Invite colleagues to set their own password and share this workspace. Only the owner manages members.')}</p></div><Users /></div>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    {notice && <div className="alert" role="status">{notice}</div>}
    {user.isMember ? <form className="team-form" onSubmit={changePassword}>
      <label>{zh ? '当前密码' : 'Current password'}<input name="currentPassword" type="password" autoComplete="current-password" required /></label>
      <label>{zh ? '新密码' : 'New password'}<input name="password" type="password" minLength={12} maxLength={256} autoComplete="new-password" required /></label>
      <label>{zh ? '确认新密码' : 'Confirm new password'}<input name="confirmPassword" type="password" minLength={12} maxLength={256} autoComplete="new-password" required /></label>
      <button className="button button-primary" disabled={busy}>{zh ? '修改并重新登录' : 'Change and sign in again'}</button>
    </form> : <>
      <p className="team-permissions">{user.role === 'admin' ? (zh ? '成员将获得完整管理员权限，包括 Agent、代理、客户与账务管理。' : 'Members receive full administrator access to agents, proxies, customers and billing.') : (zh ? '成员可使用本账号余额、任务、API 密钥及账单，不会新建独立钱包。' : 'Members share this account’s balance, tasks, API keys and billing.')}</p>
      <form className="team-form" onSubmit={create}>
        <label>{zh ? '成员姓名' : 'Member name'}<input name="displayName" maxLength={80} required /></label>
        <label>{zh ? '登录邮箱' : 'Login email'}<input name="email" type="email" maxLength={254} autoComplete="off" required /></label>
        <button className="button button-primary" disabled={busy}>{zh ? '创建邀请' : 'Create invitation'}</button>
      </form>
      {invitation && <div className="team-invitation" role="status">
        <strong>{zh ? '邀请已生成，请私下发送给同事' : 'Invitation ready — share privately'}</strong>
        <p>{zh ? '链接有效期 72 小时，仅可使用一次。对方设置密码后即可长期登录，无需 Telegram。' : 'Valid for 72 hours and one use. The member sets a password and can sign in without Telegram.'}</p>
        <label>{zh ? '邀请链接' : 'Invitation link'}<input value={invitation.invitationUrl} readOnly onFocus={event => event.currentTarget.select()} /></label>
        <button className="button button-outline" onClick={() => void navigator.clipboard.writeText(invitation.invitationUrl).then(() => setNotice(zh ? '邀请链接已复制。' : 'Invitation copied.')).catch(() => setError(zh ? '无法复制，请手动选择链接。' : 'Select the link to copy manually.'))}><Clipboard size={16} />{zh ? '复制链接' : 'Copy link'}</button>
      </div>}
      <div className="team-list">{members.map(member => <article className="team-row" key={member.id}>
        <div><strong>{member.displayName}</strong><span>{member.email}</span><small>{member.state === 'disabled' ? (zh ? '已停用' : 'Disabled') : member.passwordSet ? (zh ? '可登录' : 'Active') : (zh ? '等待设置密码' : 'Awaiting password')}</small></div>
        <div className="team-actions">
          <button className="button button-outline" disabled={busy} onClick={() => {
            if (window.confirm(zh ? '重新邀请会启用账号，并立即使旧密码和登录会话失效，继续吗？' : 'Reinvite enables this account and invalidates the old password and sessions immediately. Continue?')) void perform(async () => setInvitation(await api<Invitation>(`/api/auth/members/${member.id}/invite`, { method: 'POST', body: '{}' })))
          }}>{member.state === 'disabled' ? (zh ? '重新启用并邀请' : 'Enable and invite') : (zh ? '重新邀请' : 'Reinvite')}</button>
          {member.state === 'active' && <button className="button button-outline text-danger" disabled={busy} onClick={() => {
            if (window.confirm(zh ? '停用后该成员将立即退出，继续吗？' : 'Disable this member and revoke their sessions immediately?')) void perform(async () => { await api(`/api/auth/members/${member.id}`, { method: 'DELETE' }) })
          }}>{zh ? '停用' : 'Disable'}</button>}
        </div>
      </article>)}{!members.length && <p>{zh ? '暂时没有成员。' : 'No members yet.'}</p>}</div>
    </>}
  </section>
}

export function MemberJoinPage({ locale, go }: { locale: Locale; go: (path: string) => void }) {
  const zh = locale === 'zh-CN'
  const [token] = useState(() => location.hash.slice(1))
  useEffect(() => { history.replaceState({}, '', location.pathname) }, [])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = Object.fromEntries(new FormData(event.currentTarget))
    if (data.password !== data.confirmPassword) { setError(zh ? '两次密码不一致。' : 'Passwords do not match.'); return }
    setBusy(true); setError('')
    try {
      const result = await api<{ email: string }>('/api/auth/member-invitations/accept', { method: 'POST', body: jsonBody({ token, password: data.password }) })
      setEmail(result.email)
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Request failed.') } finally { setBusy(false) }
  }
  return <main className="member-join"><section className="content-section">
    <h1>{zh ? '加入团队' : 'Join the team'}</h1>
    <p>{zh ? '设置独立密码，之后可直接用登录邮箱和密码访问所属账号的后台。' : 'Set your password to access the account using your own login email.'}</p>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    {email ? <div role="status"><p>{zh ? `账号已启用，登录邮箱：${email}` : `Account activated. Login email: ${email}`}</p><button className="button button-primary" onClick={() => go('/login/password')}>{zh ? '前往登录' : 'Sign in'}</button></div> : !token ? <p role="alert">{zh ? '请使用账号所有者提供的有效邀请链接。' : 'Open a valid invitation link from the account owner.'}</p> : <form className="team-form" onSubmit={submit}>
      <label>{zh ? '密码（至少 12 位）' : 'Password (at least 12 characters)'}<input name="password" type="password" autoComplete="new-password" minLength={12} maxLength={256} required /></label>
      <label>{zh ? '确认密码' : 'Confirm password'}<input name="confirmPassword" type="password" autoComplete="new-password" minLength={12} maxLength={256} required /></label>
      <button className="button button-primary" disabled={busy}>{zh ? '设置密码并启用账号' : 'Set password and activate'}</button>
    </form>}
  </section></main>
}
